import React, { useMemo } from 'react';
import {
    Box,
    ButtonBase,
    Grid,
    Typography,
    lighten,
    useMediaQuery,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import moment from 'moment';
import { theme } from '../../../../shared/theme';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';

type Props = {
    recurringId: number | string;
    createdAt?: string | null;
    reservations: any[];
    onOpenReservation: (reservationId: number) => void;
    onOpenRecurring?: () => void;
};

const formatTimeWithAmPm = (t: any) => {
    const raw = String(t || '').trim();
    if (!raw || raw === '-') return '-';
    const m = moment(
        raw,
        ['H:mm', 'HH:mm', 'h:mm A', 'h:mma', 'hh:mm A'],
        true,
    );
    if (m.isValid()) return m.format('h:mm A');
    return raw;
};

const getStatusSx = (s: string) => {
    switch (s) {
        case 'approved':
            return {
                bgcolor: lighten(theme.palette.yellow.main, 0.78),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.yellow.main, 0.35),
            };
        case 'pending':
            return {
                bgcolor: lighten(theme.palette.primary.main, 0.76),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.primary.main, 0.32),
            };
        case 'cancelled':
        case 'declined':
            return {
                bgcolor: lighten(theme.palette.error.main, 0.82),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.error.main, 0.3),
            };
        case 'completed':
            return {
                bgcolor: lighten(theme.palette.info.main, 0.82),
                color: theme.palette.dark.main,
                borderColor: lighten(theme.palette.info.main, 0.3),
            };
        default:
            return {
                bgcolor: theme.palette.custom.cardActionBackground,
                color: theme.palette.text.primary,
                borderColor: theme.palette.custom.lightBorderColor,
            };
    }
};

const formatDateRange = (startDateRaw: any, endDateRaw: any) => {
    if (!startDateRaw || !endDateRaw) return '-';
    const s = moment(startDateRaw);
    const e = moment(endDateRaw);
    if (!s.isValid() || !e.isValid()) return '-';
    if (s.isSame(e, 'day')) return s.format('ddd D MMM YYYY');
    if (s.isSame(e, 'month'))
        return `${s.format('ddd D')}–${e.format('ddd D MMM YYYY')}`;
    return `${s.format('ddd D MMM')} – ${e.format('ddd D MMM YYYY')}`;
};

const formatTimeRange = (checkinTime: any, checkoutTime: any) => {
    const s = formatTimeWithAmPm(checkinTime);
    const e = formatTimeWithAmPm(checkoutTime);
    if (s === '-' && e === '-') return '-';
    if (s !== '-' && e !== '-') return `${s} → ${e}`;
    return s !== '-' ? s : e;
};

function ReservationMiniCard({
    reservation,
    onOpen,
}: {
    reservation: any;
    onOpen: () => void;
}) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const status = String(reservation?.status || '');
    const formattedStatus = status?.charAt(0).toUpperCase() + status?.slice(1);

    const dateRange = useMemo(
        () => formatDateRange(reservation?.startDate, reservation?.endDate),
        [reservation?.startDate, reservation?.endDate],
    );

    const timeRange = useMemo(
        () =>
            formatTimeRange(
                reservation?.bookingInfo?.checkinTime,
                reservation?.bookingInfo?.checkoutTime,
            ),
        [
            reservation?.bookingInfo?.checkinTime,
            reservation?.bookingInfo?.checkoutTime,
        ],
    );

    return (
        <ButtonBase
            onClick={onOpen}
            sx={{
                width: '100%',
                textAlign: 'left',
                borderRadius: 2,
                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                backgroundColor: theme.palette.neutral.main,
                overflow: 'hidden',
                '&:hover': {
                    backgroundColor: theme.palette.custom.cardActionBackground,
                },
            }}
        >
            <Box sx={{ width: '100%', p: 1.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Typography
                        component='h3'
                        sx={{
                            fontWeight: 900,
                            letterSpacing: 0.2,
                            fontSize: 14,
                            lineHeight: 1.15,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Reservation #{reservation?.id}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <CustomChip
                            label={
                                <Typography
                                    variant='body3'
                                    sx={{ fontSize: 10.5, fontWeight: 900 }}
                                >
                                    {formattedStatus}
                                </Typography>
                            }
                            variant='outlined'
                            sx={{
                                ...getStatusSx(status),
                                borderWidth: 1,
                                height: 18,
                                px: 0.6,
                                minWidth: 72,
                                borderRadius: 999,
                                '& .MuiChip-label': { px: 0.6 },
                            }}
                        />
                        <OpenInNew
                            fontSize='small'
                            sx={{
                                opacity: 0.6,
                                fontSize: 16,
                                flex: '0 0 auto',
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        backgroundColor: theme.palette.neutral.main,
                        borderRadius: 2,
                        p: 1.5,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: 0.35,
                            color: theme.palette.text.secondary,
                            lineHeight: 1.2,
                        }}
                    >
                        Check-in → Check-out
                    </Typography>

                    <Typography
                        variant='body3'
                        sx={{
                            fontSize: 14,
                            fontWeight: 900,
                            color: theme.palette.text.primary,
                            mt: 0.75,
                            lineHeight: 1.2,
                        }}
                    >
                        {dateRange}
                    </Typography>

                    {timeRange !== '-' ? (
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: theme.palette.text.secondary,
                                mt: 0.4,
                                lineHeight: 1.2,
                            }}
                        >
                            {timeRange}
                        </Typography>
                    ) : null}

                    {isMobile ? null : null}
                </Box>
            </Box>
        </ButtonBase>
    );
}

export default function RecurringReservationSummaryList({
    recurringId,
    createdAt,
    reservations,
    onOpenReservation,
    onOpenRecurring,
}: Props) {
    const createdAtText = createdAt
        ? moment(createdAt).format('DD/MM/YYYY')
        : null;

    const sorted = useMemo(() => {
        const list = Array.isArray(reservations) ? [...reservations] : [];
        list.sort((a, b) => {
            const as = moment(a?.startDate);
            const bs = moment(b?.startDate);
            if (!as.isValid() && !bs.isValid()) return 0;
            if (!as.isValid()) return 1;
            if (!bs.isValid()) return -1;
            return as.valueOf() - bs.valueOf();
        });
        return list;
    }, [reservations]);

    const stats = useMemo(() => {
        const base = {
            pending: 0,
            declined: 0,
            cancelled: 0,
            approved: 0,
            completed: 0,
        };

        for (const r of Array.isArray(reservations) ? reservations : []) {
            const s = String(r?.status || '').toLowerCase();
            if (s === 'pending') base.pending += 1;
            else if (s === 'declined') base.declined += 1;
            else if (s === 'cancelled' || s === 'canceled') base.cancelled += 1;
            else if (s === 'approved') base.approved += 1;
            else if (s === 'completed') base.completed += 1;
        }

        return base;
    }, [reservations]);

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 0.75,
                    }}
                >
                    {onOpenRecurring ? (
                        <ButtonBase
                            onClick={onOpenRecurring}
                            sx={{
                                borderRadius: 999,
                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                backgroundColor: theme.palette.neutral.main,
                                px: 1,
                                height: 26,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography sx={{ fontSize: 11, fontWeight: 900 }}>
                                View
                            </Typography>
                        </ButtonBase>
                    ) : null}
                </Box>

                <Grid container spacing={1}>
                    <Grid item xs={6} sm={4} md={2.4 as any}>
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${lighten(theme.palette.primary.main, 0.6)}`,
                                bgcolor: lighten(
                                    theme.palette.primary.main,
                                    0.82,
                                ),
                                p: 1,
                                height: '100%',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 900,
                                    letterSpacing: 0.35,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                }}
                            >
                                Pending
                            </Typography>
                            <Typography
                                sx={{ fontSize: 18, fontWeight: 900, mt: 0.4 }}
                            >
                                {stats.pending}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4 as any}>
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${lighten(theme.palette.primary.main, 0.6)}`,
                                bgcolor: lighten(
                                    theme.palette.primary.main,
                                    0.82,
                                ),
                                p: 1,
                                height: '100%',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 900,
                                    letterSpacing: 0.35,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                }}
                            >
                                Declined
                            </Typography>
                            <Typography
                                sx={{ fontSize: 18, fontWeight: 900, mt: 0.4 }}
                            >
                                {stats.declined}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2.4 as any}>
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${lighten(theme.palette.primary.main, 0.6)}`,
                                bgcolor: lighten(
                                    theme.palette.primary.main,
                                    0.82,
                                ),
                                p: 1,
                                height: '100%',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 900,
                                    letterSpacing: 0.35,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                }}
                            >
                                Cancelled
                            </Typography>
                            <Typography
                                sx={{ fontSize: 18, fontWeight: 900, mt: 0.4 }}
                            >
                                {stats.cancelled}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6} sm={6} md={2.4 as any}>
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${lighten(theme.palette.primary.main, 0.6)}`,
                                bgcolor: lighten(
                                    theme.palette.primary.main,
                                    0.82,
                                ),
                                p: 1,
                                height: '100%',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 900,
                                    letterSpacing: 0.35,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                }}
                            >
                                Approved
                            </Typography>
                            <Typography
                                sx={{ fontSize: 18, fontWeight: 900, mt: 0.4 }}
                            >
                                {stats.approved}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2.4 as any}>
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${lighten(theme.palette.primary.main, 0.6)}`,
                                bgcolor: lighten(
                                    theme.palette.primary.main,
                                    0.82,
                                ),
                                p: 1,
                                height: '100%',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 900,
                                    letterSpacing: 0.35,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                }}
                            >
                                Completed
                            </Typography>
                            <Typography
                                sx={{ fontSize: 18, fontWeight: 900, mt: 0.4 }}
                            >
                                {stats.completed}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {sorted.map((r: any) => (
                <Grid item xs={12} key={r?.id}>
                    <ReservationMiniCard
                        reservation={r}
                        onOpen={() => onOpenReservation(Number(r?.id))}
                    />
                </Grid>
            ))}
        </>
    );
}
